import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { InfoTip } from './InfoTip';
import { useSharedText } from '../lib/i18n';

/**
 * The explanation behind a heading, label or number, in an InfoTip instead of a paragraph on screen.
 * Keep on screen what the merchant needs to act; the tip answers "what is this / how is it counted".
 * `topic` names the button for screen readers ("About Attributed revenue"); pass it when the label is plain text.
 */
export function HelpTip({ help, topic }: { help?: ReactNode; topic?: ReactNode }) {
  const text = useSharedText();
  if (!help) return null;
  return <InfoTip label={text.moreInfo(typeof topic === 'string' ? topic : undefined)}>{help}</InfoTip>;
}

/** A heading with its HelpTip beside it (the tip stays outside the heading so it does not join its name). */
function Titled({ children, help, topic }: { children: ReactNode; help?: ReactNode; topic?: ReactNode }) {
  if (!help) return <>{children}</>;
  return (
    <div className="title-row">
      {children}
      <HelpTip help={help} topic={topic} />
    </div>
  );
}

/** Thin progress bar (setup checklists, usage); the visible count next to it carries the same number. */
export function ProgressBar({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div className="progress" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={value}>
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

export function usePageTitle(title: string): void {
  useEffect(() => {
    document.title = title ? `${title} · Perzo` : 'Perzo';
  }, [title]);
}

interface PageHeaderProps {
  title: string;
  /** Short status shown under the title (e.g. a demo marker); not a kicker. */
  meta?: ReactNode;
  description?: ReactNode;
  /** Explanation in an InfoTip beside the title (instead of a long description). */
  help?: ReactNode;
  actions?: ReactNode;
  back?: { to: string; label: string };
}

export function PageHeader({ title, meta, description, help, actions, back }: PageHeaderProps) {
  usePageTitle(title);
  return (
    <header className="page-header">
      <div className="page-header__text">
        {back ? (
          <Link to={back.to} className="breadcrumb">
            <ChevronLeft size={14} aria-hidden="true" />
            {back.label}
          </Link>
        ) : null}
        <Titled help={help} topic={title}>
          <h1>{title}</h1>
        </Titled>
        {description ? <p>{description}</p> : null}
        {meta ? <div className="page-header__meta">{meta}</div> : null}
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </header>
  );
}

interface PanelProps {
  title?: ReactNode;
  description?: ReactNode;
  /** Explanation in an InfoTip beside the title. */
  help?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  flush?: boolean;
  className?: string;
  id?: string;
  as?: 'section' | 'div';
}

export function Panel({ title, description, help, actions, children, footer, flush, className, id, as = 'section' }: PanelProps) {
  const Tag = as;
  const headingId = id ? `${id}-title` : undefined;
  return (
    <Tag className={`panel ${className ?? ''}`} id={id} aria-labelledby={title && headingId ? headingId : undefined}>
      {title || actions ? (
        <div className="panel__header">
          <div>
            {title ? (
              <Titled help={help} topic={title}>
                <h2 id={headingId}>{title}</h2>
              </Titled>
            ) : null}
            {description ? <p>{description}</p> : null}
          </div>
          {actions ? <div className="row">{actions}</div> : null}
        </div>
      ) : null}
      <div className={flush ? 'panel__body panel__body--flush' : 'panel__body'}>{children}</div>
      {footer ? <div className="panel__footer">{footer}</div> : null}
    </Tag>
  );
}

export function Section({ title, description, help, actions, children }: { title: string; description?: ReactNode; help?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="section">
      <div className="section__title">
        <div>
          <Titled help={help} topic={title}>
            <h2>{title}</h2>
          </Titled>
          {description ? <p>{description}</p> : null}
        </div>
        {actions ? <div className="row">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

export function Metric({
  label,
  value,
  hint,
  help,
  cost,
}: {
  label: ReactNode;
  value: ReactNode;
  /** Data shown under the number (e.g. "of 500"). Explanations go in `help`. */
  hint?: ReactNode;
  /** How the number is counted, in an InfoTip beside the label. */
  help?: ReactNode;
  /** Mark as a cost metric (amber key). */
  cost?: boolean;
}) {
  return (
    <div className={cost ? 'metric metric--cost' : 'metric'}>
      <div className="metric__label">
        {label}
        <HelpTip help={help} topic={label} />
      </div>
      <div className="metric__value">{value}</div>
      {hint ? <div className="metric__hint">{hint}</div> : null}
    </div>
  );
}

export function DescriptionList({ items }: { items: Array<[ReactNode, ReactNode]> }) {
  return (
    <dl className="dl">
      {items.map(([term, detail], index) => (
        <div key={index} style={{ display: 'contents' }}>
          <dt>{term}</dt>
          <dd>{detail}</dd>
        </div>
      ))}
    </dl>
  );
}
